import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Search, Sparkles } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { cn } from '../lib/utils';

type BetaAccessStatus = 'new' | 'in_review' | 'contacted' | 'closed';

type BetaAccessRequest = {
  id: string;
  fullName: string;
  email: string;
  companyName: string | null;
  storeDomain: string | null;
  platform: string;
  trafficOrOrderRange: string | null;
  currentSupportTools: string | null;
  biggestSupportPain: string | null;
  desiredUseCase: string | null;
  preferredContactMethod: string;
  notes: string | null;
  status: BetaAccessStatus;
  source: string;
  createdAt: string;
  updatedAt: string;
};

type BetaAccessResponse = {
  rows: BetaAccessRequest[];
  total: number;
  summary: Record<BetaAccessStatus, number> & { total: number };
};

const LIMIT = 40;

const STATUS_LABELS: Record<BetaAccessStatus | 'all', string> = {
  all: 'All',
  new: 'New',
  in_review: 'In review',
  contacted: 'Contacted',
  closed: 'Closed',
};

const STATUS_STYLES: Record<BetaAccessStatus, string> = {
  new: 'border-amber-200 bg-amber-50 text-amber-900',
  in_review: 'border-sky-200 bg-sky-50 text-sky-900',
  contacted: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  closed: 'border-zinc-200 bg-zinc-50 text-zinc-600',
};

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function platformLabel(value: string) {
  if (value === 'woocommerce') return 'WooCommerce';
  if (value === 'wordpress') return 'WordPress';
  if (value === 'shopify') return 'Shopify';
  if (value === 'squarespace') return 'Squarespace';
  if (value === 'custom') return 'Custom site';
  return value || 'Other';
}

function DetailBlock({ label, value }: { label: string; value?: string | null }) {
  if (!value?.trim()) return null;
  return (
    <div className="rounded-xl border border-zinc-200 bg-white/70 p-3">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-zinc-400">{label}</p>
      <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-zinc-700">{value}</p>
    </div>
  );
}

export default function BetaAccessRequests() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<BetaAccessStatus | 'all'>('new');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  const query = useQuery({
    queryKey: ['internal-beta-access-requests', status, search, offset, LIMIT],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('status', status);
      params.set('limit', String(LIMIT));
      params.set('offset', String(offset));
      if (search) params.set('q', search);
      return apiFetch<BetaAccessResponse>(`/api/internal/beta-access-requests?${params.toString()}`);
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, nextStatus }: { id: string; nextStatus: BetaAccessStatus }) => apiFetch(
      `/api/internal/beta-access-requests/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ status: nextStatus }) },
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-beta-access-requests'] });
    },
  });

  const rows = query.data?.rows || [];
  const total = query.data?.total || 0;
  const summary = query.data?.summary;
  const hasPrev = offset > 0;
  const hasNext = offset + LIMIT < total;

  return (
    <div className="space-y-5 p-6">
      <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950 p-6 text-white">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-sky-100">
                <Sparkles size={13} />
                Beta intake
              </div>
              <h1 className="mt-4 text-3xl font-black tracking-tight">Reach beta requests</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                Review private beta requests from the marketing site, follow up with promising stores, and move each intake card through the queue.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(['new', 'in_review', 'contacted', 'closed'] as BetaAccessStatus[]).map((key) => (
                <div key={key} className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-300">{STATUS_LABELS[key]}</p>
                  <p className="mt-1 text-2xl font-black text-white">{summary?.[key] ?? 0}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap gap-2">
              {(['new', 'all', 'in_review', 'contacted', 'closed'] as Array<BetaAccessStatus | 'all'>).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setStatus(key);
                    setOffset(0);
                  }}
                  className={cn(
                    'rounded-full border px-3.5 py-2 text-sm font-bold transition',
                    status === key
                      ? 'border-slate-950 bg-slate-950 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  )}
                >
                  {STATUS_LABELS[key]}
                </button>
              ))}
            </div>
            <form
              className="flex w-full gap-2 xl:w-[28rem]"
              onSubmit={(event) => {
                event.preventDefault();
                setOffset(0);
                setSearch(searchInput.trim());
              }}
            >
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search name, email, company, domain"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100"
                />
              </div>
              <button type="submit" className="rounded-xl bg-slate-950 px-4 text-sm font-bold text-white">
                Search
              </button>
            </form>
          </div>
        </div>
      </section>

      {query.isLoading && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 text-sm text-zinc-500">Loading beta requests...</div>
      )}
      {query.isError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">Failed to load beta requests.</div>
      )}
      {!query.isLoading && !query.isError && rows.length === 0 && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center">
          <p className="text-sm font-bold text-zinc-900">No requests in this view.</p>
          <p className="mt-1 text-sm text-zinc-500">New marketing-site submissions will appear here as amber intake cards.</p>
        </div>
      )}

      <div className="grid gap-4">
        {rows.map((request) => {
          const isNew = request.status === 'new';
          return (
            <article
              key={request.id}
              className={cn(
                'rounded-[24px] border p-5 shadow-sm transition',
                isNew
                  ? 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-sky-50 shadow-amber-100'
                  : 'border-zinc-200 bg-white'
              )}
            >
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn('rounded-full border px-2.5 py-1 text-xs font-extrabold uppercase tracking-[0.12em]', STATUS_STYLES[request.status])}>
                      {STATUS_LABELS[request.status]}
                    </span>
                    {isNew && <span className="rounded-full bg-amber-400 px-2.5 py-1 text-xs font-black uppercase tracking-[0.12em] text-amber-950">Unread</span>}
                    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-slate-500">{platformLabel(request.platform)}</span>
                    <span className="text-xs text-zinc-400">{formatDate(request.createdAt)}</span>
                  </div>
                  <h2 className="mt-3 text-xl font-black tracking-tight text-slate-950">{request.fullName}</h2>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                    <a className="inline-flex items-center gap-1 font-bold text-sky-700 hover:text-sky-900" href={`mailto:${request.email}`}>
                      <Mail size={14} />
                      {request.email}
                    </a>
                    {request.companyName && <span>{request.companyName}</span>}
                    {request.storeDomain && <span className="font-semibold text-slate-800">{request.storeDomain}</span>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-zinc-500">
                    {request.trafficOrOrderRange && <span className="rounded-full bg-zinc-100 px-2.5 py-1">{request.trafficOrOrderRange}</span>}
                    <span className="rounded-full bg-zinc-100 px-2.5 py-1">Contact: {request.preferredContactMethod}</span>
                    <span className="rounded-full bg-zinc-100 px-2.5 py-1">{request.source}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 xl:justify-end">
                  {(['in_review', 'contacted', 'closed'] as BetaAccessStatus[]).map((nextStatus) => (
                    <button
                      key={nextStatus}
                      type="button"
                      disabled={request.status === nextStatus || statusMutation.isPending}
                      onClick={() => statusMutation.mutate({ id: request.id, nextStatus })}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      Mark {STATUS_LABELS[nextStatus].toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                <DetailBlock label="What should Reach help with first?" value={request.biggestSupportPain} />
                <DetailBlock label="What would make the beta useful?" value={request.desiredUseCase} />
                <DetailBlock label="Current support tools" value={request.currentSupportTools} />
                <DetailBlock label="Notes" value={request.notes} />
              </div>
            </article>
          );
        })}
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-zinc-200 bg-white px-4 py-3">
        <p className="text-xs text-zinc-500">
          Showing {rows.length === 0 ? 0 : offset + 1}-{offset + rows.length} of {total}
        </p>
        <div className="flex gap-2">
          <button
            disabled={!hasPrev}
            onClick={() => setOffset((value) => Math.max(0, value - LIMIT))}
            className="rounded-lg border border-zinc-300 px-3 py-1.5 text-xs font-medium text-zinc-700 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            disabled={!hasNext}
            onClick={() => setOffset((value) => value + LIMIT)}
            className="rounded-lg border border-zinc-300 px-3 py-1.5 text-xs font-medium text-zinc-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
